import {PreventionInvalidityCode} from "../preventative-interactions";
import {BaseImportTransformer, toPartNumberUpper} from "./shared";

const normalizeTagNameKey = (name) => name?.trim()?.toUpperCase() ?? '';

const resolveTagNameKeyFromCell = (tagNameCell) => {
    if (tagNameCell == null) {
        return '';
    }
    if (typeof tagNameCell === 'string') {
        return normalizeTagNameKey(tagNameCell);
    }
    return normalizeTagNameKey(tagNameCell?.name);
};

const partMatchesIndustry = (partInfo, industryId) => {
    if (partInfo?.industryId == null || industryId == null) {
        return true;
    }
    return String(partInfo.industryId) === String(industryId);
};

/**
 * Assigns tags to operator parts within a single industry.
 */
export class ProductTagAssignmentImportTransformer extends BaseImportTransformer {
    /**
     * @param {Array<Object|null|undefined>} mappedData
     * @param {boolean} isFinalRun
     * @param {string|number} industryId
     */
    async run(mappedData, isFinalRun = false, industryId) {
        const rows = mappedData ?? [];
        const preventativeIssues = [];

        /** @type {{index: number, item: *, partNoUpper: string, tagNameKey: string, codes: string[]}[]} */
        const rowState = rows.map((item, index) => ({
            index,
            item,
            partNoUpper: toPartNumberUpper(item?.partNumber),
            tagNameKey: resolveTagNameKeyFromCell(item?.tagName),
            codes: [],
        }));

        for (const rs of rowState) {
            if (!rs.item) {
                continue;
            }
            if (!rs.partNoUpper || !rs.tagNameKey) {
                rs.codes.push(PreventionInvalidityCode.MISSING_REQUIRED_FIELDS);
            }
        }

        const uniqueParts = [
            ...new Set(
                rowState
                    .filter((r) => r.item && r.partNoUpper && r.tagNameKey)
                    .map((r) => r.partNoUpper),
            ),
        ].sort();

        /** @type {Record<string, *>} */
        let partIds = {};
        /** @type {Record<string, *>} */
        let partInfo = {};
        if (uniqueParts.length > 0) {
            const bundle = await this.ctx.fetchPartInfoByPartNumbers(uniqueParts);
            partIds = bundle.partIds;
            partInfo = bundle.partInfo;
        }

        for (const rs of rowState) {
            if (!rs.item || !rs.partNoUpper || !rs.tagNameKey) {
                continue;
            }
            const info = partInfo[rs.partNoUpper];
            if (!partIds[rs.partNoUpper] || !info) {
                rs.codes.push(PreventionInvalidityCode.INVALID_PART);
            } else if (!partMatchesIndustry(info, industryId)) {
                rs.codes.push(PreventionInvalidityCode.INVALID_PART);
            }
        }

        const uniqueTagNames = [
            ...new Set(
                rowState
                    .filter((r) => r.item && r.tagNameKey)
                    .map((r) => r.item.tagName)
                    .map((cell) => (typeof cell === 'string' ? cell : cell?.name ?? ''))
                    .map((n) => n?.trim())
                    .filter(Boolean),
            ),
        ];

        /** @type {Record<string, *>} */
        let tagIds = {};
        if (uniqueTagNames.length > 0 && industryId != null) {
            const tagBundle = await this.ctx.fetchTagInfoByNames(uniqueTagNames, industryId);
            tagIds = tagBundle.tagIds;
        }

        for (const rs of rowState) {
            if (!rs.item || !rs.tagNameKey) {
                continue;
            }
            if (!tagIds[rs.tagNameKey]) {
                rs.codes.push(PreventionInvalidityCode.INVALID_TAG);
            }
        }

        for (const rs of rowState) {
            if (!rs.item) {
                preventativeIssues.push({
                    rowIndex: rs.index,
                    originalRow: rows[rs.index],
                    invalidityCodes: [PreventionInvalidityCode.MISSING_REQUIRED_FIELDS],
                });
                continue;
            }
            if (!rs.codes.length) {
                continue;
            }
            preventativeIssues.push({
                rowIndex: rs.index,
                originalRow: rs.item,
                invalidityCodes: [...new Set(rs.codes)],
            });
        }

        const issueIndexSet = new Set(preventativeIssues.map((p) => p.rowIndex));

        const apiRequests = rows.map((item, index) => {
            if (!item) {
                return isFinalRun
                    ? null
                    : {partId: null, tagId: null, partNumber: null, tagName: null};
            }

            const rs = rowState[index];
            const partId = partIds[rs.partNoUpper];
            const tagId = tagIds[rs.tagNameKey];
            const tagDisplayName =
                typeof item.tagName === 'string' ? item.tagName : item.tagName?.name ?? item.tagName;

            if (issueIndexSet.has(index)) {
                return isFinalRun
                    ? null
                    : {
                        partId: partId ?? null,
                        tagId: tagId ?? null,
                        partNumber: item.partNumber,
                        tagName: tagDisplayName,
                    };
            }

            return {
                partId,
                tagId,
                partNumber: toPartNumberUpper(item?.partNumber),
                tagName: tagDisplayName,
            };
        });

        return {apiRequests, preventativeIssues};
    }
}
